/**
 *
 * Module:        Component
 * Name:          Guru Chat Search Dialog
 * Description:   Searches and opens existing Guru chats from a keyboard-friendly dialog
 * Developers:    Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright:     Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 *
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Box, Dialog, DialogContent, IconButton, InputAdornment, TextField, Typography} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {filterGuruChats} from './guruChatHistoryModel';
import {GuruChatRows} from './GuruChatList';

export default function GuruChatSearchDialog({open, chats, labels, onClose, onSelect, ...rowProps}) {
	const [search, setSearch] = useState('');
	useEffect(() => { if (!open) setSearch(''); }, [open]);
	const results = useMemo(() => filterGuruChats(chats, search), [chats, search]);
	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{sx: {borderRadius: 3, minHeight: 420}}}>
			<DialogContent sx={{p: 0}}>
				<Box sx={{display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider'}}>
					<TextField
						autoFocus
						fullWidth
						variant="standard"
						value={search}
						onChange={event => setSearch(event.target.value)}
						onKeyDown={event => { if (event.key === 'Escape') onClose(); }}
						placeholder={labels.searchChats}
						inputProps={{'aria-label': labels.searchChats}}
						InputProps={{disableUnderline: true, startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>}}
					/>
					<IconButton aria-label={labels.closeImage || 'Close'} onClick={onClose}><CloseIcon /></IconButton>
				</Box>
				<Box sx={{maxHeight: 'min(65vh, 620px)', overflowY: 'auto', py: 1}}>
					{results.length ? (
						<GuruChatRows
							{...rowProps}
							chats={results}
							labels={labels}
							onSelect={async chat => {
								const selected = await onSelect(chat);
								if (selected !== false) onClose();
							}}
						/>
					) : (
						<Typography color="text.secondary" sx={{px: 2.5, py: 4}}>{labels.noChats}</Typography>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
}
